
import Vue from 'vue'
import Tactic from '@/models/Tactic'
import Initiative from '@/models/Initiative'
import PlanningItem from '@/models/InterfacePlanningItem'
import RelatedInitiativeMapping from '@/models/RelatedInitiativeMapping'
import TagCategory from '@/models/TagCategory'
import Tag from '@/models/Tag'
import ID from '@/models/ID'
import {isExcludedByPlatformFilter} from '@/utils/platformFilter'
import TacticFieldValue from '@/models/TacticFieldValue'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import { addExDate, computeUntilDateForBreak, rebuildRruleWithNewStart } from '@/utils/recurrence'

// Inputs shared by the recurrence-break orchestration methods.
interface RecurrenceBreakOptions {
  seriesTactic: Tactic // the recurring series in the store to modify (source of truth)
  instanceStartDate: Date // the targeted occurrence's ORIGINAL series start datetime
  newStartDate: Date // the broken-out tactic's start (drag = dropped date; detail = edited)
  newEndDate: Date // the broken-out tactic's end
  contentSource?: Tactic // fully-loaded content to copy (e.g. edited TacticDetail tactic); if omitted, the series is re-fetched
  summaryContent?: string | null // pre-fetched summary HTML; undefined = resolve automatically
}

export default Vue.extend({
  computed: {
    // Use store-based Set getters for O(1) lookups (avoids redundant Set creation)
    selectedTagIntIdsSet(): Set<number> {
      return this.$store.getters.selectedTagIdSet
    },
    selectedInitiativeIntIdsSet(): Set<number> {
      return this.$store.getters.selectedInitiativeIdSet
    },
    selectedInitiativeCustomizationIntIdsSet(): Set<number> {
      return this.$store.getters.selectedInitiativeCustomizationIdSet
    },
    // Keep API ID sets for backward compatibility with existing logic
    selectedInitiativeApiIdsSet(): Set<string> {
      return new Set(
        this.$store.getters.selectedInitiatives.map((i: Initiative) => i.id.apiID)
      )
    },
    selectedInitiativeCustomizationApiIdsSet(): Set<string> {
      return new Set(
        this.$store.getters.selectedInitiativeCustomizations.map((c: any) => c.id.apiID)
      )
    },
    paidChannelNamesSet(): Set<string> {
      return this.$store.getters.paidChannelNamesSet
    },
    visibleNestedPlansIntIdsSet(): Set<number> {
      return new Set(this.$store.getters.visibleNestedPlansIntIds || [])
    },
    // Map of categoryId -> Set<tagIntId> for only the currently selected tags,
    // limited to categories that have at least one selected tag.
    selectedTagsByCategoryMap(): Map<number, Set<number>> {
      const map = new Map<number, Set<number>>()
      const selectedIds = this.selectedTagIntIdsSet
      const categories: TagCategory[] = this.$store.getters.currentPlanTagCategories || []
      categories.forEach((category: TagCategory) => {
        const selectedInCategory = new Set<number>()
        category.tags.forEach((tag: Tag) => {
          if (selectedIds.has(tag.id.intID)) {
            selectedInCategory.add(tag.id.intID)
          }
        })
        if (selectedInCategory.size > 0) {
          map.set(category.id, selectedInCategory)
        }
      })
      return map
    },

    // Optimization for date range comparisons
    currentDateRangeTimestamps(): number[] {
        const range = this.$store.getters.currentDateRange;
        return [
            range[0] ? range[0].getTime() : 0,
            range[1] ? range[1].getTime() : 0
        ];
    },
    
    // Check if filters are active only once per render
    isTagFilterActive(): boolean {
        return this.$store.getters.isTagFilterActive;
    },
    isInitiativeFilterActive(): boolean {
        return this.$store.getters.isInitiativeFilterActive;
    },
    filterNoInitiative(): boolean {
        return this.$store.getters.filterNoInitiative;
    },
    filterNoTag(): boolean {
        return this.$store.getters.filterNoTag;
    }
  },
  methods: {
    isTacticInFilteredOutPlan(tactic: Tactic | Initiative | PlanningItem): boolean {
      if (tactic.isLead && !this.$store.getters.isLeadPlanVisible) {
        return true
      }
      if (
        tactic.isNested &&
        !this.visibleNestedPlansIntIdsSet.has(tactic.planId.intID)
      ) {
        return true
      }
      return false
    },

    isTacticRelatedToDeselectedTag(tactic: Tactic): boolean {
      if (!this.isTagFilterActive) {
        return false
      }

      if (this.filterNoTag) {
        return tactic.tags.length > 0
      }

      if (tactic.tags.length === 0) {
        return true
      }

      const tacticTagIntIds = new Set(tactic.tags.map((tag: Tag) => tag.id.intID))
      // Tactic must match at least one selected tag in each category that has selected tags (AND across categories, OR within each category)
      for (const selectedTagsInCategory of this.selectedTagsByCategoryMap.values()) {
        let hasMatchInCategory = false
        for (const tagId of tacticTagIntIds) {
          if (selectedTagsInCategory.has(tagId)) {
            hasMatchInCategory = true
            break
          }
        }
        if (!hasMatchInCategory) {
          return true
        }
      }
      return false
    },

    isTacticRelatedToDeselectedPlatform(tactic: Tactic): boolean {
      return isExcludedByPlatformFilter(
        tactic.tacticPlatformIds,
        this.$store.getters.platformFilterState
      )
    },

    isTacticRelatedToDeselectedInitiative(tactic: Tactic): boolean {
      if (!this.isInitiativeFilterActive) {
        return false;
      }

      if (this.filterNoInitiative) {
        return tactic.relatedInitiatives.length > 0;
      }

      const relatedMappings = tactic.relatedInitiatives;
      if (relatedMappings.length === 0) {
        return true;
      }
      
      const relatedApiIds = relatedMappings.map((m: RelatedInitiativeMapping) => m.initiativeId.apiID);

      // Check 1: Directly selected initiative
      const hasRelatedSelectedInitiative = relatedApiIds.some(apiId => this.selectedInitiativeApiIdsSet.has(apiId));
      if (hasRelatedSelectedInitiative) return false;

      // Check 2: Directly selected customization
      const hasRelatedSelectedCustomization = relatedApiIds.some(apiId => this.selectedInitiativeCustomizationApiIdsSet.has(apiId));
      if (hasRelatedSelectedCustomization) return false;

      // Check 3: Parent initiative logic
      const selectedCustomizations = this.$store.getters.selectedInitiativeCustomizations;
      
      const hasRelatedParentWithCustomizationInPlan = selectedCustomizations.some((customization: any) => {
         if (customization.planId.intID !== tactic.planId.intID) return false;
         
         const parentApiId = customization.initiativeId.apiID;
         return relatedApiIds.includes(parentApiId);
      });

      if (hasRelatedParentWithCustomizationInPlan) return false;

      return true;
    },

    isTacticInPaidChannel(tactic: Tactic): boolean {
       return this.paidChannelNamesSet.has(tactic.channelName.toLowerCase());
    },

    isTacticLongerThanThreshold(tactic: Tactic, thresholdInDays: number): boolean {
       const msPerDay = 1000 * 60 * 60 * 24;
       
       const utc1 = Date.UTC(tactic.startDate.getFullYear(), tactic.startDate.getMonth(), tactic.startDate.getDate());
       const utc2 = Date.UTC(tactic.endDate.getFullYear(), tactic.endDate.getMonth(), tactic.endDate.getDate());

       return Math.ceil((utc2 - utc1) / msPerDay) >= thresholdInDays;
    },

    isTacticTouchingDateRange(tactic: Tactic | Initiative | PlanningItem): boolean {
        const [start, end] = this.currentDateRangeTimestamps;
        if (!start || !end) return true;
        
        return !(tactic.endDate.getTime() <= start || tactic.startDate.getTime() >= end);
    },

    isTacticExactMonth(tactic: Tactic): boolean {
        const range = this.$store.getters.currentDateRange;
        if (!range[0] || !range[1]) return false;

        return (
            range[0].getFullYear() == tactic.startDate.getFullYear() &&
            range[0].getMonth() == tactic.startDate.getMonth() &&
            range[1].getFullYear() == tactic.endDate.getFullYear() &&
            range[1].getMonth() == tactic.endDate.getMonth()
        )
    },

    // ---------------------------------------------------------------------------
    // Recurrence-break orchestration (tactics only). Shared by the calendar
    // drag/drop and TacticDetail save/delete flows. The recurrence-string mechanics
    // live in utils/recurrence; these methods tie them to the services + store.
    // ---------------------------------------------------------------------------

    // Build an unsaved Tactic copying `source`'s content for a break. When `recurring`
    // is false the clone becomes a standalone single tactic; when true it keeps the
    // series' recurrence (with the supplied rrule). Owned/collection fields are copied
    // independently so the clone never shares mutable sub-entities with the source;
    // typeValues are returned as unsaved placeholders (id 0) for the orchestrator to create.
    cloneTacticForBreak(
      source: Tactic,
      options: { recurring: boolean; startDate: Date; endDate: Date; rrule?: string | null }
    ): Tactic {
      const clone = Object.assign(new Tactic(), source) as Tactic
      clone.id = new ID()
      clone.startDate = new Date(options.startDate)
      clone.endDate = new Date(options.endDate)

      if (options.recurring) {
        clone.repeats = true
        clone.rrule = options.rrule ?? source.rrule ?? null
        clone.recurrenceEndDate = source.recurrenceEndDate ? new Date(source.recurrenceEndDate) : null
        clone.isCustomRecurrence = source.isCustomRecurrence
        clone.recurrenceText = source.recurrenceText
      } else {
        clone.repeats = false
        clone.rrule = null
        clone.recurrenceEndDate = null
        clone.isCustomRecurrence = false
        clone.recurrenceText = ''
      }

      // Independent copies of collections / owned sub-entities (id 0 = create fresh).
      clone.typeValues = source.typeValues.map(
        (value: TacticFieldValue) => new TacticFieldValue(new ID(), value.tacticFieldId, value.value)
      )
      clone.tagIds = [...source.tagIds]
      clone.tags = [...source.tags]
      clone.tacticPlatformIds = [...source.tacticPlatformIds]
      clone.tacticPlatforms = [...(source.tacticPlatforms ?? [])]
      clone.documentIds = [...source.documentIds]
      clone.documents = [...source.documents]
      clone.relatedInitiatives = source.relatedInitiatives.map(
        (mapping: RelatedInitiativeMapping) =>
          new RelatedInitiativeMapping(
            new ID(),
            mapping.initiativeId,
            mapping.initiativeTitle,
            mapping.initiativeTypeId,
            mapping.initiativeTypeName,
            mapping.initiativeIdForCustomization
          )
      )

      // The clone gets its own freshly-created summary; clear inherited refs/flags.
      clone.tacticSummary = null
      clone.summary = ''
      clone.originalTacticID = null
      clone.isRecurringEventClone = false

      return clone
    },

    // Resolve the full content source + summary HTML for a break. When no contentSource is
    // supplied (e.g. calendar drag), the series tactic is re-fetched so typeValues + summary
    // are complete (list endpoints may omit them).
    async resolveBreakSource(
      seriesTactic: Tactic,
      contentSource?: Tactic,
      summaryContent?: string | null
    ): Promise<{ source: Tactic; summaryContent: string | null }> {
      if (contentSource) {
        return {
          source: contentSource,
          summaryContent: summaryContent !== undefined ? summaryContent : (contentSource.summary || null),
        }
      }

      const full = (await this.$store.getters.services.tactics.get([seriesTactic.id.intID])) as Tactic

      // The GET endpoint only populates the *Ids arrays; the hydrated relational objects
      // (tags, tacticPlatforms, documents, relatedInitiatives) are "populated on front-end" and
      // come back empty/incomplete. Because forRequestObject serializes these from the object
      // arrays (not the Ids), a clone built from `full` would silently drop or corrupt them.
      // Restore them from the already-hydrated seriesTactic (same tactic, hydrated on plan load)
      // so breaks keep them.
      full.tags = [...seriesTactic.tags]
      full.tacticPlatforms = [...(seriesTactic.tacticPlatforms ?? [])]
      full.documents = [...seriesTactic.documents]
      full.relatedInitiatives = [...seriesTactic.relatedInitiatives]

      let resolvedSummary: string | null = summaryContent !== undefined ? summaryContent : null
      if (summaryContent === undefined && full.tacticSummary?.contentUrl) {
        resolvedSummary = await this.$store.getters.services.tactics
          .fetchSummaryContent(full.tacticSummary.contentUrl)
          .catch(() => null)
      }
      return { source: full, summaryContent: resolvedSummary }
    },

    // Persist fresh field-value entities for a clone so it never shares owned values with the
    // source. Skips empty values; a single failure is skipped rather than aborting the break.
    async createFieldValuesForBreakClone(clone: Tactic): Promise<void> {
      const created: TacticFieldValue[] = []
      for (const value of clone.typeValues) {
        if (value.value === '') continue
        try {
          const returned = (await this.$store.getters.services.tactics.createTacticFieldValue(
            value
          )) as TacticFieldValue
          created.push(returned)
        } catch (error) {
          // Skip a failed field value rather than aborting the whole break.
        }
      }
      clone.typeValues = created
    },

    // Create a summary for a freshly-saved clone, if there's content to copy.
    async copySummaryToBreakClone(savedClone: Tactic, summaryContent: string | null): Promise<void> {
      if (!summaryContent) return
      try {
        const summaryData = await this.$store.getters.services.tactics.createTacticSummary(
          savedClone.id.apiID,
          summaryContent
        )
        savedClone.tacticSummary = summaryData as Tactic['tacticSummary']
        savedClone.summary = summaryContent
      } catch (error) {
        // Summary copy failed; the tactic itself was still created.
      }
    },

    // Re-apply lead/nested flags (populated only when fetching a whole plan) from the series.
    applySeriesContextFlags(target: Tactic, seriesTactic: Tactic): void {
      target.isLead = seriesTactic.isLead
      target.isNested = seriesTactic.isNested
      target.abbreviatedPlanName = seriesTactic.abbreviatedPlanName
    },

    // Restore relational fields onto the tactic a create()/update() call returned, from the
    // trusted pre-request object (the clone we built, or the series tactic we sent). Like the GET
    // endpoint (see resolveBreakSource above), the create/update response's own
    // tags/tacticPlatforms/documents *Ids arrays aren't guaranteed to reflect everything that was
    // just sent — the store's rehydration (populateFrontEndPlanObjects) re-derives the hydrated
    // .tags/.tacticPlatforms/.documents from these *Ids arrays, so an incomplete response here
    // would silently drop them from every occurrence rendered off the saved record. Trust what we
    // actually intended to persist over the response for these fields.
    restoreTacticRelations(saved: Tactic, trusted: Tactic): void {
      saved.tagIds = [...trusted.tagIds]
      saved.tags = [...trusted.tags]
      saved.tacticPlatformIds = [...trusted.tacticPlatformIds]
      saved.tacticPlatforms = [...(trusted.tacticPlatforms ?? [])]
      saved.documentIds = [...trusted.documentIds]
      saved.documents = [...trusted.documents]
    },

    showBreakSuccessToast(summary: string): void {
      Vue.prototype.$toast.add({ severity: AlertMessageSeverity.success, summary, life: 3000 })
    },

    showBreakErrorToast(): void {
      Vue.prototype.$toast.add({
        severity: AlertMessageSeverity.error,
        summary: 'There was an error updating the recurring tactic. Please try again.',
        life: 5000,
      })
    },

    // Best-effort rollback for a break clone that was already created server-side when a LATER
    // step in the same break then fails — without this, the create would stick around as an
    // orphaned duplicate alongside the untouched/only-partially-touched original series.
    async rollbackBreakClone(clone: Tactic): Promise<boolean> {
      try {
        await this.$store.getters.services.tactics.delete([clone.id.intID])
        return true
      } catch (error) {
        return false
      }
    },

    // Error toast for a break that failed AFTER its clone was already created. Distinguishes
    // whether the rollback succeeded, since a failed rollback leaves a real duplicate the user
    // needs to notice and remove themselves.
    showBreakPartialFailureToast(rolledBack: boolean): void {
      Vue.prototype.$toast.add({
        severity: AlertMessageSeverity.error,
        summary: rolledBack
          ? 'There was an error updating the recurring tactic. No changes were made — please try again.'
          : 'There was an error updating the recurring tactic, and the new tactic it created could not be automatically removed. Please check your calendar for a duplicate tactic and delete it, then try again.',
        life: rolledBack ? 5000 : 10000,
      })
    },

    // Break THIS instance: create a standalone single tactic for the occurrence and add an
    // EXDATE to the original series so it no longer renders that date.
    async breakRecurringTacticThisInstance(options: RecurrenceBreakOptions): Promise<Tactic | null> {
      const { seriesTactic, instanceStartDate, newStartDate, newEndDate, contentSource, summaryContent } = options
      // Tracked so the catch block can roll it back if a LATER step fails — otherwise a failure
      // here after the create already succeeded leaves an orphaned duplicate tactic behind.
      let savedBroken: Tactic | null = null
      try {
        const { source, summaryContent: resolvedSummary } = await this.resolveBreakSource(
          seriesTactic,
          contentSource,
          summaryContent
        )

        const broken = this.cloneTacticForBreak(source, {
          recurring: false,
          startDate: newStartDate,
          endDate: newEndDate,
        })
        await this.createFieldValuesForBreakClone(broken)
        savedBroken = (await this.$store.getters.services.tactics.create(broken)) as Tactic
        this.applySeriesContextFlags(savedBroken, seriesTactic)
        this.restoreTacticRelations(savedBroken, broken)
        await this.copySummaryToBreakClone(savedBroken, resolvedSummary)

        seriesTactic.rrule = addExDate(seriesTactic.rrule ?? '', instanceStartDate)
        const savedSeries = (await this.$store.getters.services.tactics.update(seriesTactic)) as Tactic
        this.applySeriesContextFlags(savedSeries, seriesTactic)
        this.restoreTacticRelations(savedSeries, seriesTactic)

        await this.$store.dispatch('addTacticToCurrentPlan', savedBroken)
        await this.$store.dispatch('updateTacticInCurrentPlan', savedSeries)

        this.showBreakSuccessToast('This tactic was separated from the series.')
        return savedBroken
      } catch (error) {
        if (savedBroken) {
          const rolledBack = await this.rollbackBreakClone(savedBroken)
          this.showBreakPartialFailureToast(rolledBack)
        } else {
          this.showBreakErrorToast()
        }
        return null
      }
    },

    // Break THIS and following: create a new recurring tactic starting at this instance and
    // cap the original series to end before it (or delete the original if nothing remains).
    async breakRecurringTacticThisAndFollowing(options: RecurrenceBreakOptions): Promise<Tactic | null> {
      const { seriesTactic, instanceStartDate, newStartDate, newEndDate, contentSource, summaryContent } = options
      // Tracked so the catch block can roll it back if a LATER step fails — otherwise a failure
      // here after the create already succeeded leaves an orphaned duplicate tactic behind.
      let savedNewSeries: Tactic | null = null
      try {
        const { source, summaryContent: resolvedSummary } = await this.resolveBreakSource(
          seriesTactic,
          contentSource,
          summaryContent
        )

        // Use || (not ??) so a cleared empty-string rrule (from editing the start in TacticDetail)
        // still falls back to the unedited series' rule.
        const newSeriesRrule = rebuildRruleWithNewStart(source.rrule || seriesTactic.rrule || '', newStartDate)
        const newSeries = this.cloneTacticForBreak(source, {
          recurring: true,
          startDate: newStartDate,
          endDate: newEndDate,
          rrule: newSeriesRrule,
        })
        await this.createFieldValuesForBreakClone(newSeries)
        savedNewSeries = (await this.$store.getters.services.tactics.create(newSeries)) as Tactic
        this.applySeriesContextFlags(savedNewSeries, seriesTactic)
        this.restoreTacticRelations(savedNewSeries, newSeries)
        await this.copySummaryToBreakClone(savedNewSeries, resolvedSummary)

        const untilDate = computeUntilDateForBreak(instanceStartDate)
        if (untilDate.getTime() < seriesTactic.startDate.getTime()) {
          // Break point is at/before the first occurrence — nothing remains of the original.
          await this.$store.getters.services.tactics.delete([seriesTactic.id.intID])
          await this.$store.dispatch('removeTacticFromCurrentPlanById', seriesTactic.id)
        } else {
          seriesTactic.recurrenceEndDate = untilDate
          const savedSeries = (await this.$store.getters.services.tactics.update(seriesTactic)) as Tactic
          this.applySeriesContextFlags(savedSeries, seriesTactic)
          this.restoreTacticRelations(savedSeries, seriesTactic)
          await this.$store.dispatch('updateTacticInCurrentPlan', savedSeries)
        }

        await this.$store.dispatch('addTacticToCurrentPlan', savedNewSeries)

        this.showBreakSuccessToast('This and following tactics were separated from the series.')
        return savedNewSeries
      } catch (error) {
        if (savedNewSeries) {
          const rolledBack = await this.rollbackBreakClone(savedNewSeries)
          this.showBreakPartialFailureToast(rolledBack)
        } else {
          this.showBreakErrorToast()
        }
        return null
      }
    },

    // Delete THIS instance: add an EXDATE to the series (no new tactic created).
    async deleteRecurringTacticThisInstance(seriesTactic: Tactic, instanceStartDate: Date): Promise<boolean> {
      try {
        seriesTactic.rrule = addExDate(seriesTactic.rrule ?? '', instanceStartDate)
        const savedSeries = (await this.$store.getters.services.tactics.update(seriesTactic)) as Tactic
        this.applySeriesContextFlags(savedSeries, seriesTactic)
        this.restoreTacticRelations(savedSeries, seriesTactic)
        await this.$store.dispatch('updateTacticInCurrentPlan', savedSeries)
        this.showBreakSuccessToast('This tactic was removed from the series.')
        return true
      } catch (error) {
        this.showBreakErrorToast()
        return false
      }
    },

    // Delete THIS and following: cap the series before this instance, or delete the whole
    // series if the break point is at/before the first occurrence.
    async deleteRecurringTacticThisAndFollowing(seriesTactic: Tactic, instanceStartDate: Date): Promise<boolean> {
      try {
        const untilDate = computeUntilDateForBreak(instanceStartDate)
        if (untilDate.getTime() < seriesTactic.startDate.getTime()) {
          await this.$store.getters.services.tactics.delete([seriesTactic.id.intID])
          await this.$store.dispatch('removeTacticFromCurrentPlanById', seriesTactic.id)
        } else {
          seriesTactic.recurrenceEndDate = untilDate
          const savedSeries = (await this.$store.getters.services.tactics.update(seriesTactic)) as Tactic
          this.applySeriesContextFlags(savedSeries, seriesTactic)
          this.restoreTacticRelations(savedSeries, seriesTactic)
          await this.$store.dispatch('updateTacticInCurrentPlan', savedSeries)
        }
        this.showBreakSuccessToast('This and following tactics were removed from the series.')
        return true
      } catch (error) {
        this.showBreakErrorToast()
        return false
      }
    },
  }
})
